/**
 * @fileoverview Edit-target mutation hook.
 * @description Wraps PATCH /sales/targets/:id (Edit Target's name/period/priority correction) in a
 * TanStack mutation. Invalidates the owning tenant's target query, the broad targets list key (the
 * name, period and priority all show in Sales Pipeline's and My Page's tables), and the tenant's
 * activity history, since every effective change is recorded there as a Note.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { TARGETS_QUERY_KEY } from '@/features/sales/hooks/useTargets'
import { TENANT_ACTIVITIES_QUERY_KEY } from '@/features/sales/hooks/useTenantActivities'
import { updateTargetDetails } from '@/features/sales/services/targets.service'

import type { UpdateTargetDetailsRequest } from '@/features/sales/services/targets.service'

/**
 * @description Updates a target's name/period/priority and refreshes the affected queries.
 * @param {string} tenantId - The tenant the target belongs to, for query invalidation.
 * @returns {ReturnType<typeof useMutation<Awaited<ReturnType<typeof updateTargetDetails>>, unknown, {
 * targetId: string; input: UpdateTargetDetailsRequest }>>} The mutation object (mutate with `{
 * targetId, input }`, isPending, ...).
 */
export function useUpdateTargetDetails(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ targetId, input }: { targetId: string; input: UpdateTargetDetailsRequest }) =>
      updateTargetDetails(targetId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...TARGETS_QUERY_KEY, 'byTenant', tenantId] })
      void queryClient.invalidateQueries({ queryKey: TARGETS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: TENANT_ACTIVITIES_QUERY_KEY(tenantId) })
    },
  })
}
